import {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useMemo,
} from 'react';
import { isArrayEmpty, isObjectEmpty } from './helpers';
import { parseDuplicateParameters } from './uiUtils';
import http from './http';
import { useTranslations } from './useTranslations';
import { PARAMS } from '../constants/pages';
import { PEOPLE_DEFAULT_ORDERING } from '../constants/people';
import { SYSTEM_ROLES } from '../constants/roles';
import { USER_STATUSES } from '../constants/statuses';
import { API_USERS_BASIC } from '../constants/apiRoutes';
import { CONSENT_COOKIES } from '../constants/consent';

export const usePreviousValue = value => {
  const currentValueRef = useRef(value);
  const previousValueRef = useRef();

  if (currentValueRef.current !== value) {
    previousValueRef.current = currentValueRef.current;
    currentValueRef.current = value;
  }

  return previousValueRef.current;
};

export const useCustomEffect = (
  callback,
  dependencies = [],
  shouldRunOnMount = true
) => {
  const isMountedRef = useRef(shouldRunOnMount);

  useEffect(() => {
    if (isMountedRef.current) {
      callback();
      return undefined;
    }

    isMountedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
};

export const useAvailableUsers = (
  params = {},
  organizationUser = {},
  hasDefaultParams = false
) => {
  const [availableUsers, setAvailableMUsers] = useState([]);

  const getAvailableUsers = useCallback(async () => {
    const { EXCLUDE_ROLE, EXCLUDE_STATUS } = PARAMS;
    const { DEACTIVATED } = USER_STATUSES;

    const defaultParams = {
      [EXCLUDE_ROLE]: SYSTEM_ROLES.ASSOCIATE,
      [EXCLUDE_STATUS]: DEACTIVATED,
    };

    const { data } = await http.get(API_USERS_BASIC, {
      params: {
        ...PEOPLE_DEFAULT_ORDERING,
        ...(hasDefaultParams ? defaultParams : {}),
        ...params,
      },
      paramsSerializer: d => parseDuplicateParameters(d),
    });

    return setAvailableMUsers(data);
  }, [params, hasDefaultParams]);

  const cleanup = useCallback(() => setAvailableMUsers([]), []);

  useEffect(() => {
    if (!isObjectEmpty(params)) {
      getAvailableUsers();
    }

    return cleanup;
  }, [params, cleanup, getAvailableUsers]);

  const allUsers = useMemo(() => {
    if (isArrayEmpty(availableUsers)) return availableUsers;
    return [
      ...(organizationUser && !isObjectEmpty(organizationUser)
        ? [organizationUser]
        : []),
      ...availableUsers,
    ];
  }, [availableUsers, organizationUser]);

  return allUsers;
};

export const useResizeObserver = () => {
  const ref = useRef(null);
  const [rect, setRect] = useState(null);

  useEffect(() => {
    const observer = new ResizeObserver(() => {
      if (ref.current) {
        const boundingRect = ref.current.getBoundingClientRect();
        setRect(boundingRect);
      }
    });
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [ref]);

  return [ref, rect];
};

export const useDraggableScrollPosition = (containerRef, itemsRef) => {
  const [scrollPosition, setScrollPosition] = useState({
    scrollLeft: 0,
    scrollTop: 0,
  });

  const itemsContainer = itemsRef?.current?.children?.[0];
  const containerRect = containerRef?.current?.getBoundingClientRect();
  const itemsRect = itemsContainer?.getBoundingClientRect();
  const activeScroll = {
    top: scrollPosition.scrollTop !== 0,
    left: scrollPosition.scrollLeft !== 0,
    right:
      Math.round(containerRect?.width || 0) + scrollPosition.scrollLeft <
      Math.round(itemsRect?.width || 0),
    bottom:
      Math.round(containerRect?.height || 0) + scrollPosition.scrollTop <
      Math.round(itemsRect?.height || 0),
  };

  const handleChangeScrollPosition = useCallback(
    () =>
      setScrollPosition({
        scrollLeft: containerRef?.current?.scrollLeft || 0,
        scrollTop: containerRef?.current?.scrollTop || 0,
      }),
    [containerRef]
  );

  const onScrollToRight = () => {
    containerRef?.current?.scrollTo({
      left: itemsRect?.width,
      behavior: 'smooth',
    });
  };

  const onScrollToBottom = () => {
    containerRef?.current?.scrollTo({
      top: itemsRect?.height,
      behavior: 'smooth',
    });
  };

  const onScrollToStart = () => {
    containerRef?.current?.scrollTo({
      left: 0,
      top: 0,
      behavior: 'smooth',
    });
  };

  return [
    activeScroll,
    handleChangeScrollPosition,
    onScrollToRight,
    onScrollToBottom,
    onScrollToStart,
  ];
};

export const useCookieConsent = CookieConsent => {
  const [hasPermission, setHasPermission] = useState(false);
  const isGtmLoadedRef = useRef(null);
  const translations = useTranslations('CookieConsent');
  const GTM_ID = process.env.REACT_APP_GTM_ID;

  const initDataLayer = useCallback(() => {
    window.dataLayer = window.dataLayer || [];
  }, []);

  const onAddGoogleTagManager = useCallback(() => {
    if (isGtmLoadedRef?.current || document.getElementById('gtm-script'))
      return;
    isGtmLoadedRef.current = true;

    window.dataLayer.push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    const script = document.createElement('script');
    script.async = true;

    // Add timestamp to force reload and avoid browser caching
    const timestamp = `&ts=${Date.now()}`;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}${timestamp}`;
    script.id = 'gtm-script';

    // Insert the GTM script
    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);

    const insertNoScript = () => {
      if (!document.body) return setTimeout(insertNoScript, 50);
      if (document.getElementById('gtm-noscript')) return;

      const noscript = document.createElement('noscript');
      noscript.id = 'gtm-noscript';

      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${GTM_ID}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';

      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);
    };

    insertNoScript();
  }, [GTM_ID]);

  const onCleanup = useCallback(() => {
    window.__isControlledReload = true;
    if (window.dataLayer) {
      window.dataLayer.length = 0;
      delete window.dataLayer;
    }

    window.localStorage.clear();
    window.sessionStorage.clear();
    window.location.reload();
  }, []);

  const removeAllCookies = useCallback(() => {
    if (window.__isControlledReload) {
      window.__isControlledReload = false;
      return;
    }

    if (window.dataLayer) {
      window.dataLayer.length = 0;
      delete window.dataLayer;
    }

    CookieConsent.eraseCookies(/^(?!_pk).*$/); // Delete all but matomo cookies
    CookieConsent.reset(true);
    window.localStorage.clear();
    window.sessionStorage.clear();
  }, [CookieConsent]);

  useEffect(() => {
    CookieConsent.run({
      revision: 1,
      autoClearCookies: true,
      cookie: { name: 'kdr_app_cc_cookie' },
      guiOptions: {
        consentModal: {
          layout: 'bar',
          position: 'bottom',
          equalWeightButtons: true,
        },
        preferencesModal: {
          layout: 'box',
          position: 'left',
          equalWeightButtons: true,
        },
      },
      categories: {
        necessary: { readOnly: true, enabled: true },
        analytics: {
          autoClear: {
            cookies: [{ name: /^(_ga|_gid|_gat|_clck|_clsk|CLID|MUID)/ }],
          },
        },
      },
      language: {
        default: 'en',
        translations: {
          en: translations,
        },
      },
      onFirstConsent: () => {
        if (!window.dataLayer) initDataLayer();
        window.dataLayer.push({ event: 'consent-client-update' });
      },
      onConsent: ({ cookie }) => {
        if (!window.dataLayer) initDataLayer();
        if (cookie.categories.includes(CONSENT_COOKIES.ANALYTICS)) {
          setHasPermission(true);
          window.dataLayer.push({ event: 'consent-client-update' });
          onAddGoogleTagManager();
        }
      },
      onChange: () => {
        if (!window.dataLayer) initDataLayer();
        window.dataLayer.push({ event: 'consent-client-update' });
        onCleanup();
      },
    });
  }, [
    CookieConsent,
    translations,
    initDataLayer,
    onAddGoogleTagManager,
    onCleanup,
  ]);

  return { clearCookies: removeAllCookies, hasPermission };
};

export const useElementMaxHeight = (
  parentNode,
  shouldCalculateHeight = false,
  parentOffset = 0
) => {
  // For this to work parent node should take all available height or to have set height in px
  const [maxHeight, setMaxHeight] = useState(0);
  const elementRef = useRef(null);
  const observerRef = useRef(null);

  const calculateHeight = useCallback(() => {
    const element = elementRef.current;
    if (!element || !parentNode) return;

    const parentRect = parentNode.getBoundingClientRect();
    const elementRect = element.getBoundingClientRect();

    const parentStyle = window.getComputedStyle(parentNode);
    const paddingBottom = parseFloat(parentStyle.paddingBottom) || 0;

    const availableHeight =
      parentRect.bottom - elementRect.top - paddingBottom - parentOffset;

    if (availableHeight >= 0) {
      const finalMaxHeight = Math.floor(availableHeight);

      setMaxHeight(prevMaxHeight => {
        return Math.abs(prevMaxHeight - finalMaxHeight) > 1
          ? finalMaxHeight
          : prevMaxHeight;
      });
    }
  }, [parentNode, parentOffset]);

  const setElementRef = useCallback(
    node => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }

      if (shouldCalculateHeight && node !== null) {
        elementRef.current = node;

        calculateHeight();

        if (parentNode) {
          const observer = new ResizeObserver(() => {
            window.requestAnimationFrame(calculateHeight);
          });
          observer.observe(parentNode);
          observerRef.current = observer;
        }
      } else {
        elementRef.current = null;
      }
    },
    [shouldCalculateHeight, calculateHeight, parentNode]
  );

  useEffect(() => {
    if (shouldCalculateHeight) {
      window.addEventListener('resize', calculateHeight);
    }
    return () => {
      window.removeEventListener('resize', calculateHeight);
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [shouldCalculateHeight, calculateHeight]);

  return [setElementRef, maxHeight, calculateHeight];
};

export const useScrollMargin = (viewportRef, scrollElementRef) => {
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return undefined;
    }

    const updateScrollMargin = () => {
      const el = viewportRef.current;
      if (!el) return;

      const scrollEl = scrollElementRef?.current;
      const margin = scrollEl
        ? el.getBoundingClientRect().top -
          scrollEl.getBoundingClientRect().top +
          scrollEl.scrollTop
        : el.getBoundingClientRect().top +
          (window.scrollY || document.documentElement.scrollTop);

      setScrollMargin(margin);
    };

    updateScrollMargin();

    const scrollEl = scrollElementRef?.current;
    const scrollTarget = scrollEl ?? window;

    scrollTarget.addEventListener('scroll', updateScrollMargin, {
      passive: true,
    });
    window.addEventListener('resize', updateScrollMargin);

    const resizeObserver = new ResizeObserver(updateScrollMargin);
    resizeObserver.observe(viewport);
    if (scrollEl) {
      resizeObserver.observe(scrollEl);
    }

    return () => {
      scrollTarget.removeEventListener('scroll', updateScrollMargin);
      window.removeEventListener('resize', updateScrollMargin);
      resizeObserver.disconnect();
    };
  }, [viewportRef, scrollElementRef]);

  return scrollMargin;
};
